import React, { useState, useEffect, useMemo } from 'react';

interface LaunchBannerProps {
  /** Override launch date for testing (format: 'YYYY-MM-DDTHH:mm:ssZ') */
  launchDateOverride?: string;
}

const LaunchBanner: React.FC<LaunchBannerProps> = ({ launchDateOverride }) => {
  // Launch date: November 11, 2025 at 11:11 UTC
  const LAUNCH_DATE = useMemo(() => {
    return launchDateOverride 
      ? new Date(launchDateOverride)
      : new Date('2025-11-11T11:11:00Z');
  }, [launchDateOverride]);
  
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isLaunched: false
  });
  
  const [showLocalTime, setShowLocalTime] = useState(false);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const launchTime = LAUNCH_DATE.getTime();
      const difference = launchTime - now;

      if (difference <= 0) {
        setTimeRemaining({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isLaunched: true
        });
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds, isLaunched: false });
    };

    // Initial calculation
    calculateTimeRemaining();

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [LAUNCH_DATE]);

  // Don't show banner if already launched
  if (timeRemaining.isLaunched) {
    return null;
  }

  // Format local time
  const localTime = LAUNCH_DATE.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  });

  return (
    <div 
      className="bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 text-white shadow-md"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        height: '36px'
      }}
    >
      {/* Animated background shimmer */}
      <div 
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-10"
        style={{
          animation: 'shimmer 3s infinite',
        }}
      ></div>
      
      <div className="relative w-full h-full flex items-center justify-center px-3">
        {/* Centered content */}
        <div className="flex items-center gap-2">
          <span className="text-base animate-pulse flex-shrink-0">🚀</span>
          <span className="text-[11px] sm:text-xs font-bold whitespace-nowrap">
            LAUNCHES ON 11-11 AT 11:11 UTC
          </span>
          <button
            onClick={() => setShowLocalTime(!showLocalTime)}
            className="text-[9px] text-cyan-100 hover:text-white underline transition-colors whitespace-nowrap hidden sm:inline"
          >
            {showLocalTime ? `(${localTime})` : '(local)'}
          </button>

          {/* Countdown - ultra compact inline */}
          <div className="flex items-center gap-1 text-[11px] sm:text-xs font-bold tabular-nums flex-shrink-0">
            <span>{String(timeRemaining.days).padStart(2, '0')}</span>
            <span className="text-[9px]">:</span>
            <span>{String(timeRemaining.hours).padStart(2, '0')}</span>
            <span className="text-[9px]">:</span>
            <span>{String(timeRemaining.minutes).padStart(2, '0')}</span>
            <span className="text-[9px] hidden sm:inline">:</span>
            <span className="hidden sm:inline">{String(timeRemaining.seconds).padStart(2, '0')}</span>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}} />
    </div>
  );
};

export default LaunchBanner;

